import { action, computed, IObservableArray, makeObservable, observable, runInAction, when } from 'mobx';

import { GroupState } from '../../../../3dEngine/core/GroupState';
import { ObjectState } from '../../../../3dEngine/core/ObjectState';
import { SceneState } from '../../../../3dEngine/core/SceneState';
import { ConfiguratorCoreState } from '../../../../configurator/ConfiguratorCoreState';
import { IApiClient } from '../../../../data/client';
import {
  GetConfigurationByCodeQuery,
  GetProductByIdQuery,
  ModularComponentConfigurationData,
  ProductConfigurationData,
} from '../../../../data/model';
import type { ModularConfigurationData, MoneyData, ShoppingContext } from '../../../../data/model';
import { SiteTranslation } from '../../../../localization/SiteTranslation';
import { LoadingIndicator } from '../../../../shared/LoadingIndicator';
import { INavigationService } from '../../../../shared/NavigationService';
import { IShareModularConfiguratorUrlProvider } from '../../../../shared/ShareModularConfiguratorUrlProvider';
import ModularComponentConfiguratorState from '../ModularConfigurator/ModularComponentConfigurator/ModularComponentConfiguratorState';
import ModularConfiguratorSummaryState from './ModularConfiguratorSummary/ModularConfiguratorSummaryState';

export enum ModularConfiguratorAction {
  Configure,
  Move,
  Rotate,
}

export interface ModularComponentConfiguratorCounter {
  configuration: ModularComponentConfiguratorState;
  quantity: number;
}

class ModularConfiguratorState {
  @observable
  public activeAction: ModularConfiguratorAction;

  @observable.shallow
  public readonly components = [] as IObservableArray<ModularComponentConfiguratorState>;

  @observable
  public readonly = false;

  public readonly loadingIndicator = new LoadingIndicator();
  public readonly scene = new SceneState();
  public readonly summary: ModularConfiguratorSummaryState;

  private layoutId: string;

  @computed
  public get isLoadingConfigurationCode() {
    const componentConfigurationCodeLoading = this.components.some((x) => x.coreState.configurationCodeLoading);
    const modularConfigurationCodeLoading = this.summary.isGeneratingConfigurationCode;

    return componentConfigurationCodeLoading || modularConfigurationCodeLoading;
  }

  @computed
  public get isLoadingConfigurationCodeError() {
    const generateComponentConfigurationCodeError = this.components.some((x) => x.coreState.configurationCodeError);
    const generateModularConfigurationCodeError = this.summary.isGenerateConfigurationCodeError;

    return generateComponentConfigurationCodeError || generateModularConfigurationCodeError;
  }

  @computed
  public get usedComponentConfigurationsCounter(): ModularComponentConfiguratorCounter[] {
    return this.components.reduce<ModularComponentConfiguratorCounter[]>((accumulator, component) => {
      const indexOfComponentGroup = accumulator.findIndex(
        (x) => x.configuration.coreState.code === component.coreState.code,
      );

      if (indexOfComponentGroup === -1) {
        accumulator.push({
          quantity: 1,
          configuration: component,
        });
      } else {
        accumulator[indexOfComponentGroup].quantity++;
      }

      return accumulator;
    }, []);
  }

  @computed
  public get isConfigureActionActive() {
    return this.activeAction === ModularConfiguratorAction.Configure;
  }

  @computed
  public get isMoveActionActive() {
    return this.activeAction === ModularConfiguratorAction.Move;
  }

  @computed
  public get isRotateActionActive() {
    return this.activeAction === ModularConfiguratorAction.Rotate;
  }

  @computed
  public get selectedComponent() {
    return this.components.find((x) => x.sceneObjectsGroup.selected);
  }

  public get defaultComponentConfigurator() {
    return this.components.first();
  }

  public constructor(
    private readonly client: IApiClient,
    private readonly shoppingContext: ShoppingContext,
    private readonly translation: SiteTranslation,
    private readonly productFamilyId: string,
    navigation: INavigationService,
    configurationUrlProvider: IShareModularConfiguratorUrlProvider,
    private readonly debugMaterial: string | undefined,
  ) {
    makeObservable(this);

    this.summary = new ModularConfiguratorSummaryState(
      client,
      navigation,
      configurationUrlProvider,
      this.shoppingContext,
    );

    this.activateDefaultAction();
  }

  @action.bound
  public activateDefaultAction() {
    this.activateConfigureAction();
  }

  @action.bound
  public activateConfigureAction() {
    this.activateAction(ModularConfiguratorAction.Configure);
  }

  @action.bound
  public activateMoveAction() {
    this.activateAction(ModularConfiguratorAction.Move);
  }

  @action.bound
  public activateRotateAction() {
    this.activateAction(ModularConfiguratorAction.Rotate);
  }

  public async generateConfigurationCode() {
    await when(() => this.components.all((x) => x.coreState.configurationCodeLoading === false));

    const configuration = this.getConfigurationData();

    await this.summary.generateConfigurationCode(configuration);
  }

  public getConfigurationData(): ModularConfigurationData {
    const configuration = {
      layoutId: this.layoutId,
      productFamilyId: this.productFamilyId,
      components: this.components.map((x) => ({
        productConfigurationCode: x.coreState.code,
        productId: x.data.baseModel.id,
        position: x.sceneObjectsGroup.position,
        rotation: x.sceneObjectsGroup.rotation,
      })),
    };
    return configuration;
  }

  @action
  public async loadConfiguration(configuration: ModularConfigurationData) {
    if (!configuration) {
      return;
    }

    if (this.loadingIndicator.isLoading) {
      // TODO: just simple loading request synchronization, maybe we can do it better
      return;
    }

    this.loadingIndicator.start();

    this.scene.objects.clear();
    this.components.clear();

    this.layoutId = configuration.layoutId;

    const distinctProductsIds = configuration.components.map((x) => x.productId).distinct();
    const productsData = await Promise.all(
      distinctProductsIds.map((x) =>
        this.client.send(new GetProductByIdQuery({ productId: x, shoppingContext: this.shoppingContext })),
      ),
    );
    const componentConfigurations = await this.fetchComponentConfigurations(configuration.components);

    runInAction(async () => {
      configuration.components.forEach((component) => {
        const data = productsData.find((x) => x.baseModel.id === component.productId);
        const groupingSceneObject = new GroupState(data.baseModel.id, []);
        groupingSceneObject.position = component.position;
        groupingSceneObject.rotation = component.rotation;
        this.scene.addObject(groupingSceneObject);

        const coreState = new ConfiguratorCoreState(this.client, this.translation.configurationPage);
        coreState.initialize(data, '000000', '', [], this.shoppingContext);

        const componentConfiguration = componentConfigurations[component.productConfigurationCode];

        if (componentConfiguration) {
          coreState.restoreState(component.productConfigurationCode, {
            code: component.productConfigurationCode,
            accessories: componentConfiguration.accessories.map((x) => x.id),
            options: componentConfiguration.options.map((x) => x.id),
            quantity: componentConfiguration.quantity,
            step: '',
          });
        }

        const componentState = new ModularComponentConfiguratorState(
          data,
          groupingSceneObject,
          coreState,
          this.debugMaterial,
        );
        this.components.push(componentState);
      });

      await Promise.all(this.components.map((x) => x.coreState.ensureCodeIsInSyncWithSelectedOptionsAndAccessories()));

      await this.scene.ensureResourcesLoaded();

      this.loadingIndicator.stop();
    });
  }

  @action
  public enableReadonlyMode() {
    this.prepareReadonlyMode();
    this.readonly = true;
  }

  @action
  public disableReadonlyMode() {
    this.readonly = false;
  }

  @action
  public selectComponent(object: ObjectState) {
    if (this.readonly) {
      return;
    }

    if (object.selected) {
      return;
    }

    this.clearSelection();
    object.setSelected(true);
  }

  @action.bound
  public removeSelectedComponent() {
    if (this.readonly) {
      return;
    }

    this.removeComponent(this.selectedComponent);
  }

  @action
  public unselectComponent() {
    this.clearSelection();
  }

  @action.bound
  public applySelectedComponentConfiguration() {
    this.tryApplySimilarConfiguration(this.selectedComponent);
  }

  private tryApplySimilarConfiguration(prototype: ModularComponentConfiguratorState) {
    prototype.coreState.selectedComponents.forEach((variationCode, featureCode) => {
      const variationId = `${featureCode}-${variationCode}`;

      this.components
        .filter((component) => component !== prototype)
        .map((component) => component.coreState)
        .forEach((configurator) => {
          configurator.tabs.trySelectOptionUsingId(variationId);
          configurator.tabs.trySelectAccessoryUsingId(variationId);
        });
    });
  }

  private activateAction(activeAction: ModularConfiguratorAction) {
    if (this.readonly) {
      return;
    }

    this.activeAction = activeAction;
  }

  private prepareReadonlyMode() {
    this.unselectComponent();
  }

  @action
  private clearSelection() {
    this.scene.objects.forEach((x) => x.setSelected(false));
  }

  @action
  private removeComponent(component: ModularComponentConfiguratorState) {
    if (!component) {
      return;
    }

    this.scene.removeObject(component.sceneObjectsGroup);
    this.components.remove(component);
  }

  private async fetchComponentConfigurations(components: ModularComponentConfigurationData[]) {
    const uniqueConfigurationCodes = components.reduce<string[]>((result, { productConfigurationCode }) => {
      if (productConfigurationCode && !result.includes(productConfigurationCode)) {
        result.push(productConfigurationCode);
      }
      return result;
    }, []);
    const configurationsData = await Promise.all(
      uniqueConfigurationCodes.map((code) => this.client.send(new GetConfigurationByCodeQuery({ code }))),
    );

    return uniqueConfigurationCodes.reduce<Record<string, ProductConfigurationData>>((acc, code, index) => {
      acc[code] = configurationsData[index].productConfiguration;
      return acc;
    }, {});
  }

  @computed
  get priceDefined() {
    return this.components.length > 0 && this.components.all((x) => x.coreState.unitPrice.amount > 0);
  }

  @computed
  get canBePurchased() {
    return (
      this.components.length > 0 && this.components.all((x) => x.data.baseModel.canBePurchased) && this.priceDefined
    );
  }

  @computed
  get availableInCurrentStore() {
    return this.components.length > 0 && this.components.all((x) => x.data.baseModel.availableInCurrentStore);
  }

  @computed
  get totalPrice(): MoneyData {
    if (this.components.length === 0) {
      return { amount: 0, currency: { code: '' } };
    }
    const total = { amount: 0, currency: this.components.first().coreState.unitPrice.currency };
    total.amount = this.components.sum((x) => x.coreState.unitPrice.amount);
    return total;
  }
}

export default ModularConfiguratorState;
